// /src/store/fileManagment


// Actions types
export const START_UPLOAD = 'START_UPLOAD';
export const UPDATE_UPLOAD_PROGRESS = 'UPDATE_UPLOAD_PROGRESS';
export const UPDATE_UPLOAD_STATUS = 'UPDATE_UPLOAD_STATUS';
export const COMPLETE_UPLOAD = 'COMPLETE_UPLOAD';
export const FAIL_UPLOAD = 'FAIL_UPLOAD';
export const CANCEL_UPLOAD = 'CANCEL_UPLOAD';
export const REMOVE_UPLOAD = 'REMOVE_UPLOAD';
export const RETRY_UPLOAD = 'RETRY_UPLOAD';
export const PAUSE_UPLOAD = 'PAUSE_UPLOAD';
export const RESUME_UPLOAD = 'RESUME_UPLOAD';
export const SET_UPLOAD_CHUNK_INFO = 'SET_UPLOAD_CHUNK_INFO';

// Upload status enum
export enum UploadStatus {
  PENDING = 'pending',
  UPLOADING = 'uploading',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  RETRYING = 'retrying'
}

// File type enum pour catégoriser les fichiers
export enum FileType {
  VIDEO = 'video',
  IMAGE = 'image',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  EXECUTABLE = 'executable',
  OTHER = 'other'
}

// Informations sur un chunk
export interface ChunkInfo {
  chunkIndex: number;
  totalChunks: number;
  chunkSize: number;
  uploaded: boolean;
  retryCount: number;
}

// État d'un upload individuel
export interface UploadState {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: FileType;
  mimeType: string;
  status: UploadStatus;
  progress: number; // 0-100
  uploadedBytes: number;
  startTime: Date;
  endTime?: Date;
  estimatedTimeRemaining?: number; // en secondes
  uploadSpeed?: number; // bytes per second
  error?: string;
  retryCount: number;
  maxRetries: number;
  
  // Informations sur les chunks
  chunks: ChunkInfo[];
  currentChunkIndex: number;
  
  // Métadonnées additionnelles
  metadata?: Record<string, any>;
  
  // Informations sur la destination
  entityId?: number; // ID de l'entité associée (video, album, etc.)
  entityType?: string; // Type d'entité (video, image, etc.)
}

// État global du store pour les uploads
export interface FileUploadState {
  uploads: { [uploadId: string]: UploadState };
  activeUploadsCount: number;
  totalUploadsCount: number;
  globalProgress: number; // Progression globale de tous les uploads actifs
  maxConcurrentUploads: number;
  isGloballyPaused: boolean;
}

// État initial
export const initialState: FileUploadState = {
  uploads: {},
  activeUploadsCount: 0,
  totalUploadsCount: 0,
  globalProgress: 0,
  maxConcurrentUploads: 3, // Max 3 uploads simultanés par défaut
  isGloballyPaused: false,
};

// Interfaces pour les actions
export interface StartUploadAction {
  type: typeof START_UPLOAD;
  payload: {
    id: string;
    fileName: string;
    fileSize: number;
    fileType: FileType;
    mimeType: string;
    totalChunks: number;
    chunkSize: number;
    maxRetries?: number;
    metadata?: Record<string, any>;
    entityId?: number;
    entityType?: string;
  };
}

export interface UpdateUploadProgressAction {
  type: typeof UPDATE_UPLOAD_PROGRESS;
  payload: {
    id: string;
    progress: number;
    uploadedBytes: number;
    uploadSpeed?: number;
    estimatedTimeRemaining?: number;
  };
}

export interface UpdateUploadStatusAction {
  type: typeof UPDATE_UPLOAD_STATUS;
  payload: {
    id: string;
    status: UploadStatus;
    error?: string;
  };
}

export interface CompleteUploadAction {
  type: typeof COMPLETE_UPLOAD;
  payload: {
    id: string;
  };
}

export interface FailUploadAction {
  type: typeof FAIL_UPLOAD;
  payload: {
    id: string;
    error: string;
  };
}

export interface CancelUploadAction {
  type: typeof CANCEL_UPLOAD;
  payload: {
    id: string;
  };
}

export interface RemoveUploadAction {
  type: typeof REMOVE_UPLOAD;
  payload: {
    id: string;
  };
}

export interface RetryUploadAction {
  type: typeof RETRY_UPLOAD;
  payload: {
    id: string;
  };
}

export interface PauseUploadAction {
  type: typeof PAUSE_UPLOAD;
  payload: {
    id: string;
  };
}

export interface ResumeUploadAction {
  type: typeof RESUME_UPLOAD;
  payload: {
    id: string;
  };
}

export interface SetUploadChunkInfoAction {
  type: typeof SET_UPLOAD_CHUNK_INFO;
  payload: {
    id: string;
    chunkIndex: number;
    uploaded: boolean;
    retryCount?: number;
  };
}

// Union type pour toutes les actions
export type FileUploadActionTypes = 
  | StartUploadAction
  | UpdateUploadProgressAction
  | UpdateUploadStatusAction
  | CompleteUploadAction
  | FailUploadAction
  | CancelUploadAction
  | RemoveUploadAction
  | RetryUploadAction
  | PauseUploadAction
  | ResumeUploadAction
  | SetUploadChunkInfoAction;